import { Component, EventEmitter, Input, Output } from '@angular/core';
import { BusinessClient, ClientType, PersonClient } from '@models/clients';
import {
	MatterClientRelationship,
	MatterClientType,
	MatterFileType,
} from '@models/matter';
import { UntilDestroy } from '@ngneat/until-destroy';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { SelectedMatter } from '@state/matter/matter.selectors';
import LogRocket from 'logrocket';
import { ICON } from '@core/util/icon';
import { BusinessState } from '@state/business';
import { NameFormattingSettings } from '@models/businessSettings/settings';
import { MatterClientDispatch } from '@state/matter_clients/matterClients.dispatch';
import { clientLegalName } from '@core/util/client';
import { TestHooks } from './create-client-side-form.hooks';

const allClientTypes = [
	ClientType.PERSON,
	ClientType.CORPORATION,
	ClientType.BUILDER,
];
@UntilDestroy()
@Component({
	selector: 'app-aps-create-client-side-form',
	templateUrl: './create-client-side-form.component.html',
	standalone: false,
})
export class CreateClientSideFormComponent {
	testHooks = TestHooks;
	public clientTypes = ClientType;
	public _selectableClientTypes = allClientTypes;
	public firstName: string;
	public middleName: string;
	public lastName: string;
	public phone: string;
	public workPhone: string;
	private _name: string;
	public enableSigningOfficers = false;
	public showClientTypeOption = true;
	public showIDsOption = false;
	public showRepresentationOption = true;
	public ICON = ICON;
	private _createdId;

	public data: PersonClient | BusinessClient;

	get name(): string {
		return this._name;
	}

	@Input() clientName: string;
	@Input() clientType = ClientType.PERSON;
	@Input() matterClientType: MatterClientType;
	@Input() primaryContact;
	@Input() showPrimaryClientOption = true;
	@Input() minify = false;

	@Output() onCreate = new EventEmitter();
	@Output() onCancel = new EventEmitter();

	@SelectSnapshot(SelectedMatter.matterFileType) fileType: MatterFileType;
	@SelectSnapshot(BusinessState.getNameFormattingSettings)
	nameFormattingSettings: NameFormattingSettings;

	constructor(private matterClientsDispatch: MatterClientDispatch) {}

	_onCreate = async (
		data: { primary: boolean } & (
			| Partial<PersonClient>
			| Partial<BusinessClient>
		),
	) => {
		this.data = data as PersonClient | BusinessClient;

		try {
			this._createdId = data.id;
			this.primaryContact = data.primary;
			const relationship: Partial<MatterClientRelationship> = {
				clientTypes: [this.matterClientType],
				primaryContact: this.primaryContact,
			};

			if (this.isClient) {
				relationship.client = this.isClient;
			}

			const clientCreationData = {
				client: this.data,
				matterClientRelationship: {
					...relationship,
					offeror: clientLegalName(this.data, this.nameFormattingSettings),
				},
			};

			if (this.matterClientType === MatterClientType.BUYER) {
				this.matterClientsDispatch.CreateAndAddClientToMatterWithOfferor(
					clientCreationData,
				);
			} else {
				this.matterClientsDispatch.CreateAndAddClientToMatter(
					clientCreationData,
				);
			}

			this.onCreate.emit(this.data.id);
		} catch (err) {
			LogRocket.error('unable to create client', err);
		}
	};

	_onCancel = () => {
		this.onCancel.emit();
	};

	setClientType(client) {
		this.clientType = client;
	}

	get showPersonOption(): boolean {
		return this._selectableClientTypes.includes(ClientType.PERSON);
	}

	showCorporationOption(): boolean {
		return this._selectableClientTypes.includes(ClientType.CORPORATION);
	}

	get showBuilderOption(): boolean {
		return this._selectableClientTypes.includes(ClientType.BUILDER);
	}

	get isClient() {
		return (
			(this.fileType === MatterFileType.PURCHASE &&
				this.matterClientType === MatterClientType.BUYER) ||
			(this.fileType === MatterFileType.SALE &&
				this.matterClientType === MatterClientType.SELLER) ||
			(this.fileType === MatterFileType.REFINANCE &&
				this.matterClientType === MatterClientType.BORROWER)
		);
	}
}
